import * as $ from "jquery";
import * as Backbone from "backbone";
import * as Marionette from "backbone.marionette";
import {on, debounce, Options} from "../utils/decorators";
import * as Cookies from 'js-cookie';
import Favorites from '../favorites/favorites';

const IE11 = window.navigator.userAgent.indexOf('Trident/7.0') > -1;

interface Favorite {
    performanceID: number,
    timestamp: number,
}

@Options({
    template: false,
    ui: {

    },
})
export default class FavoriteButton extends Marionette.View<Backbone.Model>{
    performanceID:number;
    timestamp:number;
    showText:boolean;

    checkLocalFavsForNextRender:boolean = false;
    favoritesCache:Favorite[] = [];
    showCircle:boolean = false;
    dontListen:boolean = false; // will disable the event listener, so you can handle events yourself

    trueCount:number = 0;

    constructor(options?:object) {
        super(options);

        let el = (this.$el as any);

        // check options object for settings; if they're not in there, check data attributes
        this.performanceID = options['performanceID'] || parseInt(el.attr('data-tessitura-id'));
        this.timestamp = options['timestamp'] || parseInt(el.attr('data-timestamp'));
        this.showCircle = options.hasOwnProperty('showCircle') ? options['showCircle'] : ((el.attr('data-show-circle') == 'true'));
        this.showText = options.hasOwnProperty('showText') ? options['showText'] : ((el.attr('data-show-text') == 'true'));
        this.dontListen = options.hasOwnProperty('dontListen') ? options['dontListen'] : false;

        // if you pass favorites through the options object, will use those for
        // the first render, instead of checking cookie. will save some processing time!
        if(options['favorites']) {
            this.checkLocalFavsForNextRender = true;
            this.favoritesCache = options['favorites'];
        }

        // if the favorites cookie doesnt exist, set it as an empty list
        let favorites = Cookies.get('favorites');
        if(!favorites) {
            Favorites.setFavoritesCookie([]);
            this.trueCount = 0;
        } else {
            this.trueCount = favorites.length;
        }

        // only attach one listener to this, rather than more every time it is rendered.
        // originally i was going to let hybrid-list-view rerender only the changed
        // favorites... but then i realised that only re-rendering the current button
        // is basically good enough, since favorites probably won't change through
        // other means apart from in certain circumstances

        // if you need to make ALL favorites buttons on the calendar change when the
        // favorites are updated, please go to hybrid-list-view.ts and find the event
        // listener there and do it the smart way to prevent a lot of rendering lag
        if(!this.dontListen) {
            if(IE11) {
                console.warn('older browser detected, setting up loop to check for favorites changing');
                setInterval(() => {
                    let favorites = Favorites.getFavorites();
                    if (favorites.length != this.trueCount) {
                        this.render();
                        this.trueCount = favorites.length;
                    }
                }, 100);
            } else {
                window.addEventListener('favoritesChanged', event => {
                    this.render();
                    let favorites = Favorites.getFavorites();
                    this.trueCount = favorites.length;
                });
            }
        }
    }


    onRender() {
        let icons = this.getIcons();
        let isInFavorites = this.checkLocalFavsForNextRender ?
            (this.favoritesCache.find(x => x.performanceID == this.performanceID) ? true : false)
            : Favorites.isInFavorites(this.performanceID);
        const filledHeart = this.showCircle ? icons.circled : icons.filled;
        let template = isInFavorites ? filledHeart : icons.empty;

        if (this.showText) {
            template += `<div class="favorite-link">FAVORITE</div>`
        }

        this.$el.html(template);

        this.$el[0].classList.remove('favorited');
        this.$el[0].classList.remove('favorited--circle');

        if(isInFavorites) {
            this.$el[0].classList.add('favorited');

            if(this.showCircle) {
                this.$el[0].classList.add('favorited--circle');
            }
        }

        this.checkLocalFavsForNextRender = false;
    }

    @on("click")
    onClick(e) {
        // could possible pull out the cookie reading so that
        // we only read the cookie once on click?
        if(Favorites.isInFavorites(this.performanceID)) {
            Favorites.unfavorite(this.performanceID);
            this.render();
        } else {
            Favorites.favorite(this.performanceID, this.timestamp);
            this.render();
        }
        e.preventDefault();
    }

    getIcons() {
        let animationTime = 0.25; // in seconds

        let random = Math.floor(Math.random() * 100000000);

        let emptyHeart:string = `
        <svg id="Group_91" data-name="Group 91" xmlns="http://www.w3.org/2000/svg" width="20.575" height="19.139" viewBox="0 0 20.575 19.139">
            <path id="Path_188" data-name="Path 188" d="M18.908,1.654A5.791,5.791,0,0,0,14.881,0a5.737,5.737,0,0,0-4.595,2.333A5.551,5.551,0,0,0,8.272.625,5.6,5.6,0,0,0,5.691,0,5.791,5.791,0,0,0,1.664,1.654,5.7,5.7,0,0,0,0,5.667,8.742,8.742,0,0,0,.5,8.534a5.967,5.967,0,0,0,.625,1.227c.279.43.538.787.756,1.072a10.346,10.346,0,0,0,.951,1.052c.42.41.766.733,1.018.961s.635.568,1.139,1.008c.578.5.992.877,1.257,1.116s.635.592,1.126,1.062a17.308,17.308,0,0,1,2.417,2.834.568.568,0,0,0,.491.272.591.591,0,0,0,.514-.272,17.911,17.911,0,0,1,1.553-2.013q.908-1.013,1.509-1.576c.407-.38.995-.911,1.751-1.587.467-.417.844-.733,1.1-.961s.582-.548.985-.951a12.591,12.591,0,0,0,.951-1.039c.232-.292.477-.649.756-1.072A6.714,6.714,0,0,0,20.42,7.15a7.186,7.186,0,0,0,.155-1.476A5.721,5.721,0,0,0,18.908,1.654Zm.35,5.129a7.247,7.247,0,0,1-1.9,3.576c-.464.511-1.311,1.328-2.551,2.43-.642.571-1.16,1.039-1.566,1.412s-.877.85-1.455,1.466a18.269,18.269,0,0,0-1.5,1.805A20.4,20.4,0,0,0,8.729,15.7c-.595-.608-1.052-1.072-1.388-1.378s-.857-.763-1.566-1.388l-.756-.669-.689-.625c-.3-.269-.528-.491-.679-.655s-.339-.373-.578-.635a5.83,5.83,0,0,1-.548-.679,6.966,6.966,0,0,1-.83-1.432l-.249-.776A5.67,5.67,0,0,1,1.2,5.667,4.245,4.245,0,0,1,2.518,2.528,4.354,4.354,0,0,1,5.691,1.213a4.345,4.345,0,0,1,2.4.7A4.433,4.433,0,0,1,9.751,3.785a.551.551,0,0,0,.538.339.57.57,0,0,0,.558-.339,4.437,4.437,0,0,1,1.63-1.872,4.494,4.494,0,0,1,5.57.6,4.271,4.271,0,0,1,1.314,3.153A6.718,6.718,0,0,1,19.257,6.783Z" transform="translate(0 0)" fill="currentColor"/>
        </svg>
        `;

        let filledHeart:string = `
        <svg xmlns="http://www.w3.org/2000/svg" width="20.568" height="19.138" viewBox="0 0 20.568 19.138">
            <path id="path-fill-${this.performanceID}-${random}" data-name="Path 188" d="M279.664,157.638a5.707,5.707,0,0,0-8.621.679,5.745,5.745,0,0,0-4.6-2.333,5.8,5.8,0,0,0-4.02,1.654,5.707,5.707,0,0,0-1.671,4.014,8.732,8.732,0,0,0,.5,2.871,6,6,0,0,0,.626,1.223c.279.431.538.787.757,1.072a10.49,10.49,0,0,0,.952,1.052c.421.411.767.732,1.018.962s.636.574,1.14,1c.574.5.992.877,1.258,1.115s.636.591,1.125,1.062a17.3,17.3,0,0,1,2.416,2.834.572.572,0,0,0,.491.273.589.589,0,0,0,.514-.273,18.117,18.117,0,0,1,1.546-2.011q.909-1.012,1.509-1.579c.406-.38.995-.912,1.751-1.586.467-.416.844-.732,1.1-.962s.581-.548.985-.952a12.278,12.278,0,0,0,.952-1.038c.233-.287.477-.649.757-1.072a6.686,6.686,0,0,0,1.018-2.518,7.076,7.076,0,0,0,.155-1.476A5.718,5.718,0,0,0,279.664,157.638Zm-8.618,2.464" transform="translate(-260.757 -155.984)" fill="currentColor" stroke="none" opacity="0" />
            <animate xlink:href="#path-fill-${this.performanceID}-${random}" attributeName="opacity" from="0" to="1" dur="${animationTime}s" fill="freeze" />
        </svg>
        `;

        let circledHeart:string = `
        <svg id="Component_29_1" data-name="Component 29 – 1" xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
            <g id="Group_3143" data-name="Group 3143" transform="translate(0 0)">
                <g id="Group_2835" data-name="Group 2835" transform="translate(0 0)">
                <g id="ellipse-${this.performanceID}-${random}" data-name="Ellipse 46" fill="none" stroke="currentColor" stroke-width="1" opacity="0">
                    <circle cx="20" cy="20" r="20" stroke="none"/>
                    <circle cx="20" cy="20" r="19.5" fill="none"/>
                </g>
                </g>
            </g>
            <path id="path-fill-${this.performanceID}-${random}" data-name="Path 188" d="M279.664,157.638a5.707,5.707,0,0,0-8.621.679,5.745,5.745,0,0,0-4.6-2.333,5.8,5.8,0,0,0-4.02,1.654,5.707,5.707,0,0,0-1.671,4.014,8.732,8.732,0,0,0,.5,2.871,6,6,0,0,0,.626,1.223c.279.431.538.787.757,1.072a10.49,10.49,0,0,0,.952,1.052c.421.411.767.732,1.018.962s.636.574,1.14,1c.574.5.992.877,1.258,1.115s.636.591,1.125,1.062a17.3,17.3,0,0,1,2.416,2.834.572.572,0,0,0,.491.273.589.589,0,0,0,.514-.273,18.117,18.117,0,0,1,1.546-2.011q.909-1.012,1.509-1.579c.406-.38.995-.912,1.751-1.586.467-.416.844-.732,1.1-.962s.581-.548.985-.952a12.278,12.278,0,0,0,.952-1.038c.233-.287.477-.649.757-1.072a6.686,6.686,0,0,0,1.018-2.518,7.076,7.076,0,0,0,.155-1.476A5.718,5.718,0,0,0,279.664,157.638Zm-8.618,2.464" transform="translate(-251.049 -144.356)" fill="currentColor" stroke="none" opacity="0" />
            <animate xlink:href="#ellipse-${this.performanceID}-${random}" attributeName="opacity" from="0" to="1" dur="${animationTime}s" fill="freeze" />
            <animate xlink:href="#path-fill-${this.performanceID}-${random}" attributeName="opacity" from="0" to="1" dur="${animationTime}s" fill="freeze" />
        </svg>
        `;

        return {
            empty: emptyHeart,
            filled: filledHeart,
            circled: circledHeart,
        }
    }
}



// WEBPACK FOOTER //
// ./@modules/views/favorite-button.ts